// src/pages/admin/AdminLeakage.js
// Tableau de bord "Fuite" (désintermédiation) — Phase 0 (mesure)
// Maxula
import React, { useEffect, useState, useCallback } from 'react';
import { FiTrendingDown, FiShoppingBag, FiDollarSign, FiActivity, FiInfo } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { metricsAPI } from '../../services/api';

const PERIODS = [
  { v: 7, label: '7 jours' },
  { v: 30, label: '30 jours' },
  { v: 90, label: '90 jours' }
];

const fmtNum = (n) => new Intl.NumberFormat('fr-FR').format(Number(n || 0));

// Construit une série journalière complète sur `days` jours (0 pour les jours
// sans événement), pour que la courbe de tendance soit lisible — le backend ne
// renvoie que les jours actifs (série creuse).
const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const buildDailySeries = (byDay, days) => {
  const map = {};
  (byDay || []).forEach((r) => { map[String(r.date).slice(0, 10)] = Number(r.count) || 0; });
  const out = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const dt = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
    const key = dayKey(dt);
    out.push({ date: key, count: map[key] || 0 });
  }
  return out;
};
const fmtMAD = (n) => `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(Number(n || 0))} MAD`;

const CONTEXT_LABELS = {
  deal_order: 'Bouton « Commander via WhatsApp »',
  deal_seller: 'Carte vendeur (WhatsApp)',
  merchant_page: 'Page boutique',
  autre: 'Autre'
};
const CHANNEL_LABELS = { whatsapp: 'WhatsApp', tel: 'Téléphone', email: 'Email' };

const Kpi = ({ icon, label, value, sub, tone = 'gray' }) => {
  const tones = {
    gray: 'bg-gray-50 text-gray-700',
    red: 'bg-red-50 text-red-600',
    green: 'bg-green-50 text-green-600',
    blue: 'bg-blue-50 text-blue-600',
    amber: 'bg-amber-50 text-amber-600'
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone]}`}>{icon}</div>
        <div className="min-w-0">
          <div className="text-xs text-gray-500">{label}</div>
          <div className="text-xl font-bold text-gray-900 truncate">{value}</div>
        </div>
      </div>
      {sub && <div className="mt-2 text-xs text-gray-400">{sub}</div>}
    </div>
  );
};

const Bars = ({ rows, labels }) => {
  const max = Math.max(1, ...rows.map((r) => r.n));
  if (rows.length === 0) return <div className="text-sm text-gray-400 py-2">Aucune donnée.</div>;
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.key}>
          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
            <span>{(labels && labels[r.key]) || r.key}</span>
            <span className="font-semibold text-gray-900">{fmtNum(r.n)}</span>
          </div>
          <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full rounded-full bg-primary-500" style={{ width: `${(r.n / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const AdminLeakage = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (d) => {
    try { setLoading(true); const res = await metricsAPI.leakage({ days: d }); setData(res.data?.data || null); }
    catch (e) { setData(null); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(days); }, [days, load]);

  const channelRows = data ? Object.entries(data.byChannel || {}).map(([key, n]) => ({ key, n })).sort((a, b) => b.n - a.n) : [];
  const contextRows = data ? Object.entries(data.byContext || {}).map(([key, n]) => ({ key, n })).sort((a, b) => b.n - a.n) : [];
  const trend = buildDailySeries(data?.byDay, days);
  const trendMax = Math.max(1, ...trend.map((t) => t.count));

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Fuite (désintermédiation)</h1>
      <p className="text-sm text-gray-500 mb-5">Mesure des intentions de contact hors-plateforme (clics WhatsApp / tél / email vers un marchand). Phase de mesure — aucune porte n'est encore fermée.</p>

      <div className="flex flex-wrap gap-2 mb-5">
        {PERIODS.map((p) => (
          <button
            key={p.v}
            onClick={() => setDays(p.v)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              days === p.v ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" /></div>
      ) : !data ? (
        <div className="bg-white rounded-2xl border border-gray-200 py-16 text-center text-gray-400">Aucune donnée disponible.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Kpi tone="red" icon={<FiTrendingDown size={18} />} label="Intentions de fuite" value={fmtNum(data.totalIntents)} sub={`sur ${data.days} jours`} />
            <Kpi tone="green" icon={<FaWhatsapp size={18} />} label="Part WhatsApp" value={`${data.whatsappShare}%`} sub={`${fmtNum(data.whatsappIntents)} clic(s) WhatsApp`} />
            <Kpi tone="blue" icon={<FiShoppingBag size={18} />} label="Commandes payées" value={fmtNum(data.paidOrdersCount)} sub={`${fmtNum(data.ordersCount)} commande(s) au total`} />
            <Kpi tone="amber" icon={<FiDollarSign size={18} />} label="GMV on-platform" value={fmtMAD(data.paidGMV)} sub="Volume payé via la plateforme" />
            <Kpi
              tone="red"
              icon={<FiActivity size={18} />}
              label="Ratio fuite / vente payée"
              value={data.leakageRatio == null ? '—' : `${data.leakageRatio}×`}
              sub={data.leakageRatio == null ? 'Des intentions, mais 0 vente payée (fuite totale)' : 'Intentions hors-plateforme par vente payée'}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="text-sm font-semibold text-gray-900 mb-3">Par canal</div>
              <Bars rows={channelRows} labels={CHANNEL_LABELS} />
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="text-sm font-semibold text-gray-900 mb-3">Par emplacement</div>
              <Bars rows={contextRows} labels={CONTEXT_LABELS} />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-5 mt-4">
            <div className="text-sm font-semibold text-gray-900 mb-3">Tendance (intentions / jour)</div>
            {trend.length === 0 ? (
              <div className="text-sm text-gray-400 py-2">Aucune donnée.</div>
            ) : (
              <div className="flex items-end gap-1 h-32">
                {trend.map((t) => (
                  <div key={t.date} className="flex-1 h-full flex flex-col items-center justify-end" title={`${t.date} : ${t.count}`}>
                    <div className="w-full rounded-t bg-primary-400" style={{ height: `${(t.count / trendMax) * 100}%`, minHeight: t.count > 0 ? 4 : 0 }} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-5 flex items-start gap-2 text-xs text-gray-500 bg-gray-50 rounded-xl p-3">
            <FiInfo className="shrink-0 mt-0.5" />
            <span>
              « Intention de fuite » = un acheteur a cliqué pour contacter un marchand hors-plateforme (WhatsApp/tél/email). Plus le ratio fuite/vente est élevé, plus tu perds de commission potentielle. Cet écran sert à décider quelles portes fermer en priorité (voir le plan d'architecture).
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminLeakage;
